// "Approvals are waiting" notice, its data hook, and the nav count badge.
// Shown only to people who may approve (canManageWorkspace, the same
// permission the backend's approve/deny routes require).
import React from "react";
import {
  approvalsNoticeText,
  dismissApprovalsNotice,
  fetchPendingApprovals,
  isApprovalsNoticeDismissed,
  resetApprovalsNoticeDismissal,
} from "../lib/pendingApprovals";
import { makeNameLookup } from "../lib/auditLogs";
import "./PendingApprovals.css";

export const APPROVALS_VIEW_PATH = "/audit-logs?view=approvals";

const POLL_MS = 5 * 60 * 1000;
const FOCUS_THROTTLE_MS = 60 * 1000;
const EMPTY = { count: 0, approvals: [], complete: true, loaded: false };

/**
 * Pending approvals for the signed-in approver. Fetches on start, when the
 * tab becomes visible again (at most once a minute) and every 5 minutes.
 */
export function usePendingApprovals({ enabled = false, workspaceKey = "" } = {}) {
  const [state, setState] = React.useState(EMPTY);
  const [dismissed, setDismissed] = React.useState(() => isApprovalsNoticeDismissed());
  const lastFetchRef = React.useRef(0);
  const controllerRef = React.useRef(null);

  const refresh = React.useCallback(async () => {
    if (!enabled) return null;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    lastFetchRef.current = Date.now();
    try {
      const next = await fetchPendingApprovals({ signal: controller.signal });
      if (!controller.signal.aborted) setState({ ...next, loaded: true });
      return next;
    } catch {
      // Keep the last good answer; the next poll tries again.
      return null;
    }
  }, [enabled]);

  React.useEffect(() => {
    if (!enabled) {
      setState(EMPTY);
      return undefined;
    }
    // Re-read on every (re)start: the sign-in page clears the flag, and this
    // hook may have been mounted since before that sign-in.
    setDismissed(isApprovalsNoticeDismissed());
    refresh();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, POLL_MS);
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastFetchRef.current > FOCUS_THROTTLE_MS
      ) {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      controllerRef.current?.abort();
    };
  }, [enabled, workspaceKey, refresh]);

  const dismiss = React.useCallback(() => {
    dismissApprovalsNotice();
    setDismissed(true);
  }, []);

  return {
    count: enabled ? state.count : 0,
    approvals: enabled ? state.approvals : [],
    complete: state.complete,
    loaded: state.loaded,
    dismissed,
    dismiss,
    refresh,
  };
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="7.2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M10 6.2V10l2.7 1.7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m5.5 5.5 9 9m0-9-9 9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** The dismissible notice at the top of the hub. */
export function PendingApprovalsBanner({
  count = 0,
  approvals = [],
  teamMembers = [],
  onReview,
  onDismiss,
}) {
  const nameFor = React.useMemo(() => makeNameLookup(teamMembers), [teamMembers]);
  if (!count) return null;
  const text = approvalsNoticeText(approvals, count, nameFor);
  return (
    <div className="approvalsNotice" role="status" aria-live="polite">
      <span className="approvalsNotice__icon">
        <ClockIcon />
      </span>
      <div className="approvalsNotice__copy">
        <strong>{text}</strong>
        <span>Nothing in the approval queue runs until you approve or deny it.</span>
      </div>
      <div className="approvalsNotice__actions">
        <button type="button" className="btn primary approvalsNotice__review" onClick={onReview}>
          Review approvals
        </button>
        <button
          type="button"
          className="approvalsNotice__close"
          onClick={onDismiss}
          aria-label="Dismiss until next sign-in"
          title="Dismiss until next sign-in"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}

/** Small count on the Audit Logs nav item. */
export function NavCountBadge({ count = 0, label = "approvals waiting" }) {
  if (!count) return null;
  const shown = count > 99 ? "99+" : String(count);
  return (
    <span className="navCountBadge" aria-label={`${count} ${label}`} title={`${count} ${label}`}>
      {shown}
    </span>
  );
}

/** On the sign-in page: the next sign-in shows the notice again. */
export function ResetApprovalsNotice() {
  React.useEffect(() => {
    resetApprovalsNoticeDismissal();
  }, []);
  return null;
}
